import { cloneDeep } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import { AppContext } from "../../../../core/context/appContextProvider";
import useRole from "../../../../core/routing/useRole";
import { approvePendingChanges, checkInvestorNameAvailability, declinePendingChanges, getInvestor, saveInvestor } from "../../../../services/investor.service";
import {
  CHANGES_APPROVED_SUCCESS_MESSAGE,
  CHANGES_DECLINE_SUCCESS_MESSAGE,
  ERROR_TAKEN_INVESTOR_NAME,
  GENERIC_ERROR_MESSAGE,
  INVESTORS_NO_FUNDS_ERROR,
} from "../../../../utils/constants/text.constants";
import { LoadingStatus } from "../../../../utils/types/form.type";
import { InvestorDetail } from "../../../../utils/types/investor.type";
import { ScopeRole } from "../../../../utils/types/user.type";
import { DEFAULT_INVESTOR, DEFAULT_WIRE_INFO, SAVE_INVESTOR_ERROR, SAVE_INVESTOR_SUCCESS } from "../InvestorList.constants";

type Props = {
  investor?: InvestorDetail;
  isNewInvestor: boolean;
  setIsSubmitting: Function;
  onClose: Function
}

export const useInvestorDetails = ({
  investor,
  isNewInvestor,
  setIsSubmitting,
  onClose
}: Props) => {
  const [isLoading, setIsLoading] = useState<LoadingStatus | ''>();
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);
  const [originalName, setOriginalName] = useState<string | undefined>('');
  const [originalInvestor, setOriginalInvestor] = useState<InvestorDetail | undefined>();

  const [title, setTitle] = useState<string | undefined>('');
  const [isUniqueNameError, setIsUniqueNameError] = useState(false);

  const { informationAlert, setAppHasChanges } = useContext(AppContext);

  const { hasRole: isClientPortalUser } = useRole([ScopeRole.BASIC_USER]);

  useEffect(() => {
    setAppHasChanges(isDirty);
  }, [investor]);

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors
    },
    control,
    watch,
    reset,
    resetField,
    setValue,
  } = useForm<InvestorDetail>({
    defaultValues: investor ?? DEFAULT_INVESTOR
  });
  const {
    isDirty
  } = useFormState({
    control
  });

  useEffect(() => {
    setAppHasChanges(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (investor && !isNewInvestor) {
      if (!investor.wireInfo) {
        investor.wireInfo = DEFAULT_WIRE_INFO;
      }

      let fullName = '';

      if (investor.entityType === 'INDIVIDUAL') {
        fullName = investor.middle
          ? `${investor.firstName} ${investor.middle} ${investor.lastName}`
          : `${investor.firstName} ${investor.lastName}`;

      } else if (investor.entityType === 'ENTITY' && investor.name) {
        fullName = investor.name;
      }

      if (investor.ownerSSN) {
        setValue('ownerSSN', investor.ownerSSN.replace(/\D/g, ''));
      }
      if (investor.taxIdentificationNumber) {
        setValue('taxIdentificationNumber', investor.taxIdentificationNumber.replace(/\D/g, ''));
      }

      setOriginalName(fullName);
      setTitle(investor.fullName || investor.name);
      setOriginalInvestor({ ...investor });
    }
  },
    [investor?.id]
  );

  const createUpdateInvestor = async (data: any) => {
    if (data.funds.length === 0) { return; }

    const unsetInvestorType = data.fundInvestorTypes.find((t:any) => !t.investorType.label);

    if(unsetInvestorType) return;


    deleteEmptyProps(data.wireInfo);
    deleteEmptyProps(data.wireInfo.beneficiaryBankInfo);
    deleteEmptyProps(data.wireInfo.receivingBankInfo);
    deleteEmptyProps(data.wireInfo.intermediaryBankInfo);

    if (data.ownerSSN === '') {
      data.ownerSSN = null;
    }

    if (data.taxIdentificationNumber === '') {
      data.taxIdentificationNumber = null;
    }

    if (data.ownerSSN) {
      data.ownerSSN = data.ownerSSN.replace('-', '');
    }

    if (data.taxIdentificationNumber) {
      data.taxIdentificationNumber = data.taxIdentificationNumber.replace('-', '');
    }

    const investorData = {
      ...data
    };

    if (!data.id) {
      investorData.canEditWireInfo = false;
      investorData.canReadWireInfo = false;
      investorData.className = "investor";
      investorData.clientId = "";
      investorData.email = "";
      investorData.fax = "";
      investorData.isPrimary = false;
      investorData.joinedQuarter = "";
      investorData.pendingWireInfo = null;
      investorData.status = "ACTIVE";
    }

    let fullName = '';

    if (data.entityType === 'INDIVIDUAL') {
      fullName = data.middle
        ? `${data.firstName} ${data.middle} ${data.lastName}`
        : `${data.firstName} ${data.lastName}`;

    } else if (data.entityType === 'ENTITY' && data.name) {
      fullName = data.name;
    }

    if ((fullName && originalName !== fullName && data.id) || (fullName && isNewInvestor)) {
      isNewInvestor ? setIsLoading(LoadingStatus.Adding) : setIsLoading(LoadingStatus.Updating);

      try {
        const isNameAvailable = await checkInvestorNameAvailability(fullName);

        if (!isNameAvailable) {
          informationAlert(ERROR_TAKEN_INVESTOR_NAME, "error");
          setIsUniqueNameError(true);
          return;
        }
      } catch (error) {
        informationAlert(GENERIC_ERROR_MESSAGE, "error");
        return;
      } finally {
        setIsLoading('');
      }
    }

    if (isClientPortalUser) {
      investorData.clientPortalWireInfo = { ...data.wireInfo };
      investorData.wireInfoClient = data.pendingWireInfo ? { ...data.pendingWireInfo } : { ...data.wireInfo };
      investorData.wireInfo = { ...originalInvestor?.wireInfo };
      investorData.pendingWireInfo = { ...originalInvestor?.pendingWireInfo };
      deleteEmptyProps(investorData.wireInfo);
      deleteEmptyProps(investorData.pendingWireInfo);
    }


    const clonedInvestorData = cloneDeep(investorData);

    clonedInvestorData.fundInvestorTypes = investorData.fundInvestorTypes.map((t:any) => {
      return {
        "fundId": t.fundId,
        "investorType": t.investorType.id
      };
    });


    try {
      setIsSubmitting(true);
      isNewInvestor ? setIsLoading(LoadingStatus.Adding) : setIsLoading(LoadingStatus.Updating);

      await saveInvestor(clonedInvestorData);
      informationAlert(SAVE_INVESTOR_SUCCESS, "success");

      closeDrawer();
    } catch (error: any) {
      informationAlert(SAVE_INVESTOR_ERROR, "error");
      if (error && error.response && error.response.data && error.response.data.errors) {
        if (error.response.data.errors.length > 0) {
          error.response.data.errors.map((err: { field: string, message: string }) => {
            if (err.field === 'taxIdentificationNumber') {
              setError(err.field, { type: 'custom', message: err.message });
            }
            if (err.field === 'ownerSSN') {
              setError(err.field, { type: 'custom', message: err.message });
            }
          });
        }
      }
    } finally {
      setIsLoading('');
      setIsSubmitting(false);
    }
  };

  const toggleDrawer = () => {
    if (isDirty) {
      setShowExitConfirmation(true);
    } else {
      closeDrawer();
    }
  };

  const closeDrawer = () => {
    reset();
    onClose();
    setShowExitConfirmation(false);
  };

  const keepDrawerOpen = () => {
    setShowExitConfirmation(false);
  };

  const approveChanges = async () => {
    if (investor && investor.id) {
      try {
        setIsSubmitting(true);
        await approvePendingChanges(
          investor.pendingWireInfo?.id,
          investor.id
        );
        setIsSubmitting(false);
        closeDrawer();
        informationAlert(CHANGES_APPROVED_SUCCESS_MESSAGE, "success");
      } catch (err) {
        informationAlert(INVESTORS_NO_FUNDS_ERROR, "error");
      }
    }
  };

  const declineChanges = async () => {
    if (investor && investor.id) {
      try {
        setIsSubmitting(true);
        await declinePendingChanges(investor.id);
        setIsSubmitting(false);
        closeDrawer();
        informationAlert(CHANGES_DECLINE_SUCCESS_MESSAGE, "success");
      } catch (err) {
        informationAlert(INVESTORS_NO_FUNDS_ERROR, "error");
        setIsSubmitting(false);
      }
    }
  };

  const deleteEmptyProps = (obj: any): any => {
    return Object.keys(obj).forEach((k) => {
      if (
        !obj[k] ||
        obj[k] === undefined ||
        (Array.isArray(obj[k]) && obj[k].length === 0)
      ) {
        delete obj[k];
      }
    });
  };

  return {
    form: {
      register,
      control,
      handleSubmit,
      setError,
      errors,
      setValue,
      watch,
      reset,
      resetField
    },
    isLoading,
    setIsLoading,
    createUpdateInvestor,
    investor,
    showExitConfirmation,
    setShowExitConfirmation,
    title,
    isUniqueNameError,
    setIsUniqueNameError,
    toggleDrawer,
    closeDrawer,
    keepDrawerOpen,
    approveChanges,
    declineChanges
  };
};

export const useTabs = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const detailsRef = useRef<HTMLInputElement>(null);
  const fundsRef = useRef<HTMLInputElement>(null);
  const wiringRef = useRef<HTMLInputElement>(null);
  const pageBottomRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (event: any, newValue: any) => {
    setCurrentTab(newValue);
    switch (newValue) {
      case 0:
        detailsRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case 1:
        fundsRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case 2:
        wiringRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
    }
  };

  const scrollToBottom = () => {
    pageBottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return {
    currentTab,
    detailsRef,
    fundsRef,
    wiringRef,
    pageBottomRef,
    handleTabChange,
    scrollToBottom
  };
};
